import { FormEvent, KeyboardEvent, ReactNode, useEffect, useRef, useState } from 'react';

import styled from 'styled-components';

import { Button } from '@components/button';
import { IconReturn } from '@components/icons/return';
import { SizedRelativeSpinner } from '@components/loading-spinner';
import { StandaloneMenuItem } from '@components/menu';
import { PopoverScreen } from '@components/popover-screens';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useIsMountedRef } from '@hooks/use-is-mounted-ref';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';

import { getInlineEditErrorMessage, isServerSlug } from './get-inline-edit-error-message';
import { editableCellLocale } from './locale';
import { useCellActions } from './use-cell-actions';

import { AvailableColumn, DataItemType } from '../types';

export type EditableCellPopoverProps = {
  trigger: ReactNode;
  column: AvailableColumn<any>;
  row: DataItemType;
  value: any;
  isCommitOnClick?: boolean;
  isSaveDisabled?: boolean;
  validationError?: string;
  // Set when the editor shows the message on the field itself, the way the
  // designs do - the panel then only uses it to keep Save disabled.
  isValidationErrorInField?: boolean;
  // The return icon on Save promises that Enter saves, which only holds for an
  // editor whose field submits the form. Clear it where it doesn't: a file
  // editor hands focus to its dropzone, where Enter reopens the file picker.
  isSaveOnEnter?: boolean;
  onSave?(): Promise<void>;
  onOpen?(): void;
  onCancel?(): void;
  children: ReactNode | ((close: () => void) => ReactNode);
};

type EditableCellTranslate = ReturnType<typeof useScopedTranslate<typeof editableCellLocale>>['t'];

const getSaveErrorMessage = (error: unknown, t: EditableCellTranslate): string => {
  const slug = getInlineEditErrorMessage(error);
  const localeEn = editableCellLocale.en;

  if (slug && slug in localeEn) {
    return t(slug as keyof typeof localeEn);
  }

  if (slug && !isServerSlug(slug)) {
    return slug;
  }

  return t('saveFailed');
};

export const useInlineSave = (options?: { onError?(message: string): void }) => {
  const { t } = useScopedTranslate(editableCellLocale);
  const isMountedRef = useIsMountedRef();
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const save = async (fn: () => Promise<void>, close: () => void) => {
    setIsSaving(true);
    try {
      await fn();
      close();
    } catch (caughtError) {
      if (isMountedRef.current) {
        const message = getSaveErrorMessage(caughtError, t);
        setError(message);
        options?.onError?.(message);
      }
    } finally {
      if (isMountedRef.current) {
        setIsSaving(false);
      }
    }
  };

  return { isSaving, error, save };
};

type PopoverContentProps = Omit<EditableCellPopoverProps, 'trigger'> & {
  isOpen: boolean;
  close(): void;
};

const PopoverContent = ({
  isOpen,
  close,
  column,
  row,
  value,
  isCommitOnClick = false,
  isSaveDisabled = false,
  validationError,
  isValidationErrorInField = false,
  isSaveOnEnter = true,
  onSave,
  onOpen,
  onCancel,
  children,
}: PopoverContentProps) => {
  const { t } = useScopedTranslate(editableCellLocale);
  const isMountedRef = useIsMountedRef();
  const panelRef = useRef<HTMLElement | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [serverError, setServerError] = useState<string | undefined>(undefined);
  const [isEditing, setIsEditing] = useState(false);

  const actions = useCellActions({
    column,
    row,
    value,
    openEditor: () => setIsEditing(true),
    close,
  });
  const isShowingActions = actions.length > 0 && !isEditing;

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setServerError(undefined);
    setIsEditing(false);
    onOpen?.();

    // Escape and Enter are only handled from inside the panel, so an editor
    // whose first field doesn't take focus on open (phone, multiselect, date,
    // file) would be unreachable by keyboard. Give the panel itself focus,
    // after a frame so an autofocusing field keeps it.
    const frame = requestAnimationFrame(() => {
      if (panelRef.current && !panelRef.current.contains(document.activeElement)) {
        panelRef.current.focus({ preventScroll: true });
      }
    });

    return () => cancelAnimationFrame(frame);
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const body = typeof children === 'function' ? children(close) : children;

  const handleCancel = () => {
    close();
    onCancel?.();
  };

  if (isShowingActions) {
    return (
      <ActionsPanel
        data-c="editable-cell-panel"
        data-screen="actions"
        ref={(element) => {
          panelRef.current = element;
        }}
        tabIndex={-1}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.stopPropagation();
            handleCancel();
          }
        }}
      >
        {actions.map((action) => (
          <StandaloneMenuItem
            key={action.key}
            label={action.label}
            icon={action.icon}
            variant={action.variant}
            onSelect={action.onSelect}
            dataTestId={`cell-action-${action.key}`}
          />
        ))}
      </ActionsPanel>
    );
  }

  if (isCommitOnClick) {
    return (
      <CommitOnClickPanel
        data-c="editable-cell-panel"
        ref={(element) => {
          panelRef.current = element;
        }}
        tabIndex={-1}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.stopPropagation();
            handleCancel();
          }
        }}
      >
        {body}
        {validationError && (
          <Text
            font="regular12"
            color="error"
            text={validationError}
            data-c="editable-cell-error"
          />
        )}
      </CommitOnClickPanel>
    );
  }

  const handleSave = async (event: FormEvent) => {
    event.preventDefault();
    if (isSaveDisabled || validationError || isSaving) {
      return;
    }
    setIsSaving(true);
    try {
      await onSave?.();
      close();
    } catch (error) {
      if (isMountedRef.current) {
        setServerError(getSaveErrorMessage(error, t));
      }
    } finally {
      if (isMountedRef.current) {
        setIsSaving(false);
      }
    }
  };

  // Escape is cancel for the whole editor: fields that swallow it themselves
  // (the option search box, the country dropdown) never let it reach the
  // popover. Enter needs wiring too, since a form only submits implicitly from
  // a field and the panel itself is what holds focus for several editors.
  const handlePanelKeyDown = (event: KeyboardEvent<HTMLFormElement>) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      handleCancel();
      return;
    }
    if (event.key === 'Enter' && event.target === event.currentTarget) {
      handleSave(event);
    }
  };

  const errorMessage = (isValidationErrorInField ? undefined : validationError) ?? serverError;

  const getSaveIcon = () => {
    if (isSaving) {
      return <SizedRelativeSpinner size={16} $skipMargin $borderWidth={2} />;
    }
    return isSaveOnEnter ? <IconReturn /> : undefined;
  };

  return (
    <FormPanel
      data-c="editable-cell-panel"
      ref={(element) => {
        panelRef.current = element;
      }}
      tabIndex={-1}
      onKeyDown={handlePanelKeyDown}
      onSubmit={handleSave}
    >
      <Stack space={4}>
        {body}
        {errorMessage && (
          <Text font="regular12" color="error" text={errorMessage} data-c="editable-cell-error" />
        )}
        <Stack align="flex-end">
          <Stack orientation="horizontal" space={2}>
            <Button text={t('core.cancel')} variant="secondary" onClick={handleCancel} />
            <Button
              text={t('core.save')}
              variant="primary-blue"
              iconRight={getSaveIcon()}
              type="submit"
              disabled={isSaveDisabled || isSaving}
            />
          </Stack>
        </Stack>
      </Stack>
    </FormPanel>
  );
};

// The designs draw every single-field editor at 320px, wide enough for an
// email address to read in full - the panel otherwise shrinks to its field.
export const FieldEditorBody = styled.div({ minWidth: pxToRem(320) });

// An option list loads its options after the panel is already open, so the
// panel has to hold the menu's width from the first frame - otherwise it opens
// as a box the size of the loading row and jumps once the options land. The
// width is fixed rather than a minimum because a search box only appears past a
// threshold of options, and an input carries an intrinsic width of about 20
// characters: left to grow, the same list opens wider with a search box than
// without one.
export const OptionEditorBody = styled.div({
  display: 'flex',
  flexDirection: 'column',
  width: pxToRem(224),
  // Let the search box shrink into that width instead of setting it.
  '.search-box-container, .search-box-container input': {
    minWidth: 0,
  },
});

// The panel takes focus itself when no field inside it does, and that focus
// should stay invisible.
const FormPanel = styled.form({ outline: 'none' });

const CommitOnClickPanel = styled.div({ outline: 'none' });

const ActionsPanel = styled.div({
  outline: 'none',
  display: 'flex',
  flexDirection: 'column',
  minWidth: pxToRem(192),
});

export const EditableCellPopover = ({ trigger, ...rest }: EditableCellPopoverProps) => {
  return (
    <PopoverScreen
      panelOverflow="visible"
      // The designs hang the panel off the left edge of the cell, and off the
      // right one where the viewport leaves no room - which is flip()'s
      // cross-axis behaviour for an aligned placement, with shift() as the
      // backstop for a panel wider than the space either way.
      placement="bottom-start"
      trigger={
        <Button variant="ghost" size="sm" data-c="editable-cell-trigger">
          {trigger}
        </Button>
      }
      contentStyle={{ width: 'auto', maxWidth: 'none' }}
      offset={{ mainAxis: 1 }}
      isShiftedIntoView
    >
      {({ close, isOpen }: { close(): void; isOpen: boolean }) => (
        <PopoverContent isOpen={isOpen} close={close} {...rest} />
      )}
    </PopoverScreen>
  );
};
